import React from "react"
import { Box, Tooltip } from "@mui/material"
import WarningAmberIcon from "@mui/icons-material/Warning"
import { DataGrid } from "@mui/x-data-grid"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { ConvertDDToDM, normaliseLng } from "@/utils/geoFormat"
import { nowMs } from "@/utils/appNow"
import { rowIssues } from "@/features/table/waypointGridModel.js"
import { aboveLimit, formatWeatherValue, waypointWeather } from "@/features/route/waypointWeather.jsx"
import { CAUTION, DANGER } from "@/theme/tokens"
import { alpha } from "@mui/material/styles"

dayjs.extend(utc)

function formatValueDir(flow, param) {
  if (flow?.speed == null) return ""
  const v = formatWeatherValue(flow.speed, param)
  return flow.dir == null ? v : `${v} / ${Math.round(flow.dir)}°`
}

function timestampMs(ts) {
  if (!ts) return NaN
  // Numbers are UNIX seconds, everything else an ISO-like string.
  const d = typeof ts === "number" ? new Date(ts * 1000) : new Date(ts)
  return d.getTime()
}

// "Entry 27 Oct 04:00 (WP 3 → WP 4)"; a route end reads "Entry at departure" / "Exit at arrival".
function endpointText(label, end, flag, atEnd) {
  if (end?.[flag]) return `${label} ${atEnd}`
  if (!end?.time) return null
  return `${label} ${dayjs.utc(end.time).format("DD MMM HH:mm")}${end.leg ? ` (${end.leg})` : ""}`
}

const spanLines = (transit) =>
  [
    endpointText("Entry", transit.entry, "atDeparture", "at departure"),
    endpointText("Exit", transit.exit, "atArrival", "at arrival"),
  ].filter(Boolean)

function warningIcon(items, color) {
  return (
    <Tooltip
      title={
        <Box>
          {items.map((item, i) => (
            <Box key={i} sx={{ "& + &": { mt: 0.5 } }}>
              <div>{item.text}</div>
              {item.span?.map((line) => (
                <Box key={line} sx={{ opacity: 0.8, whiteSpace: "nowrap" }}>{line}</Box>
              ))}
            </Box>
          ))}
        </Box>
      }
      placement="right"
      arrow
    >
      <WarningAmberIcon sx={{ fontSize: 14, color }} />
    </Tooltip>
  )
}

const COLUMNS = [
  {
    field: "number",
    headerName: "WP",
    description:
      "Waypoint number. Decimal suffix (e.g. 5.2) marks an intermediate weather/RNW point inserted between original waypoints.",
    width: 50,
    valueGetter: (value, row) => {
      const n = row.properties.number
      const ext = row.properties.extended_number
      return ext ? `${n}.${ext}` : String(n)
    },
  },
  {
    field: "info",
    headerName: "Info",
    description:
      "Optional waypoint name (port/landmark) plus warning and speed-limit indicators. Hover icons for details.",
    width: 110,
    valueGetter: (value, row) => row.properties.name ?? "",
    renderCell: (params) => {
      const issues = params.row?.issues ?? []
      const warnings = issues.filter((i) => i.level === "warning")
      const infos = issues.filter((i) => i.level === "info")
      return (
        // Icons first: a long name must not push them out of the narrow cell.
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, height: "100%" }}>
          {warnings.length > 0 && warningIcon(warnings, DANGER)}
          {infos.length > 0 && warningIcon(infos, CAUTION)}
          {params.value && (
            <Box component="span" title={params.value} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
              {params.value}
            </Box>
          )}
        </Box>
      )
    },
  },
  {
    field: "timestamp",
    headerName: "Time (UTC)",
    description: "ETA at this waypoint in UTC (YYYY-MM-DD HH:mm).",
    width: 120,
    valueGetter: (value, row) => row.properties.timestamp,
    renderCell: (params) => {
      if (!params.value) return ""
      const d = dayjs.utc(params.value)
      return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : ""
    },
  },
  {
    field: "sog",
    headerName: "SOG (kn)",
    description:
      "Speed over ground (knots). Comes from the pathfinder response, sensor report, or Albis report.",
    width: 80,
    valueGetter: (value, row) => {
      const p = row.properties
      const sog =
        p?.sog ?? p?.albis_sensors?.speedOverGround_kn ?? p?.albis_reports?.speedOverGround_kn
      return sog != null && Number.isFinite(Number(sog)) ? Number(sog).toFixed(1) : ""
    },
  },
  {
    field: "heading",
    headerName: "HDG (°)",
    description:
      "Sensor heading (reported) or course to the next waypoint (planned) in degrees (0°=N, 90°=E).",
    width: 75,
    valueGetter: (value, row) => {
      const p = row.properties
      const hdg = p?.heading ?? p?.albis_sensors?.headingTrue_deg ?? p?.course_bearing
      return hdg != null && Number.isFinite(Number(hdg)) ? `${Math.round(Number(hdg))}°` : ""
    },
  },
  {
    field: "lat",
    headerName: "Latitude",
    description: "Latitude in degrees + decimal minutes (WGS84). Suffix N = north, S = south.",
    width: 95,
    valueGetter: (value, row) => {
      const [lon, lat] = row.geometry.coordinates
      return ConvertDDToDM([normaliseLng(lon), lat])[0]
    },
  },
  {
    field: "lng",
    headerName: "Longitude",
    description: "Longitude in degrees + decimal minutes (WGS84). Suffix E = east, W = west.",
    width: 100,
    valueGetter: (value, row) => {
      const [lon, lat] = row.geometry.coordinates
      return ConvertDDToDM([normaliseLng(lon), lat])[1]
    },
  },
  {
    field: "legtype",
    headerName: "Leg",
    description:
      "Leg geometry type from this waypoint to the next. GC = great circle, RL = rhumbline.",
    width: 60,
    valueGetter: (value, row) => {
      const raw = row.properties.legtype ?? row.properties.legType
      if (raw === 1 || raw === "1") return "rhumbline"
      if (raw === 0 || raw === "0") return "greatcircle"
      return typeof raw === "string" ? raw.toLowerCase() : raw
    },
    renderCell: (params) => {
      const v = params.value
      if (!v) return ""
      const short = v === "greatcircle" ? "GC" : v === "rhumbline" ? "RL" : v
      const color = "#004f7d"
      return (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            height: 18,
            px: 0.75,
            fontSize: 12,
            fontWeight: 600,
            color,
            borderRadius: "10px",
            border: `1px solid ${color}55`,
            backgroundColor: `${color}11`,
          }}
        >
          {short}
        </Box>
      )
    },
  },
  {
    field: "wind",
    headerName: "Wind (kn / °)",
    description:
      "Wind speed (knots) and direction (°, meteorological: direction the wind is coming FROM, 0°=N).",
    width: 100,
    headerClassName: "col-separator-left",
    cellClassName: "col-separator-left",
    valueGetter: (value, row) => formatValueDir(waypointWeather(row.properties).wind, "wind"),
  },
  {
    field: "wave",
    headerName: "Wave (m / °)",
    description:
      "Significant wave height (m) and mean wave direction (°, direction waves are coming FROM, 0°=N).",
    width: 100,
    valueGetter: (value, row) => formatValueDir(waypointWeather(row.properties).wave, "wave"),
  },
  {
    field: "current",
    headerName: "Current (kn / °)",
    description:
      "Ocean current speed (knots) and set direction (°, direction current flows TOWARDS, 0°=N).",
    width: 110,
    valueGetter: (value, row) => formatValueDir(waypointWeather(row.properties).current, "current"),
  },
].map((c) => ({ ...c, sortable: false, editable: false }))

const GRID_SX = {
  overscrollBehavior: "contain",
  fontSize: 12,
  "& .MuiDataGrid-columnHeaderTitle": { fontSize: 12, fontWeight: 600 },
  "& .MuiDataGrid-virtualScroller": { overscrollBehavior: "contain" },
  "& .col-separator-left": { borderLeft: "1px solid rgba(0, 0, 0, 0.2)" },
  "& .row-past": { backgroundColor: "rgba(0, 0, 0, 0.04)", color: "rgba(0, 0, 0, 0.6)" },
  "& .row-alert": { backgroundColor: alpha(DANGER, 0.12) },
  "& .row-warn": { backgroundColor: alpha(DANGER, 0.08) },
  "& .row-info": { backgroundColor: alpha(CAUTION, 0.08) },
}

const NO_TRANSITS = []

// Read-only waypoint grid for one route or draft. Rows are tinted by passed time, wind/wave limit
// breaches and their issues: the route's transits and leg warnings, as on the map (rowIssues).
export default function RouteWaypointGrid({ voyagePoints, windLimit, waveLimit, transits = NO_TRANSITS }) {
  const rows = React.useMemo(() => {
    const features = (voyagePoints?.features ?? []).map((row, index) => ({
      ...row,
      properties: {
        ...row.properties,
        number: row.properties?.number ?? index + 1,
        extended_number: row.properties?.extended_number ?? 0,
      },
    }))
    const byId = Object.fromEntries(transits.map((t) => [t.id, t]))
    const issues = rowIssues(features, transits).map((row) =>
      row.map((issue) => (issue.transitId ? { ...issue, span: spanLines(byId[issue.transitId]) } : issue)),
    )
    return features.map((row, i) => ({ ...row, issues: issues[i] }))
  }, [voyagePoints, transits])

  const rowClassName = React.useCallback(
    (params) => {
      const props = params.row?.properties || {}
      const tsMs = timestampMs(props.timestamp)
      const isPast = !Number.isNaN(tsMs) && tsMs < nowMs()

      const { wind, wave } = waypointWeather(props)
      // Compares the shown (rounded) value, as the cards do.
      const isAlert = aboveLimit(wind?.speed, windLimit, "wind") || aboveLimit(wave?.speed, waveLimit, "wave")

      const issues = params.row?.issues ?? []
      const hasWarn = issues.some((i) => i.level === "warning")
      const hasInfo = !hasWarn && issues.length > 0

      const classNames = []
      if (isPast) classNames.push("row-past")
      if (isAlert) classNames.push("row-alert")
      else if (hasWarn) classNames.push("row-warn")
      else if (hasInfo) classNames.push("row-info")
      return classNames.join(" ")
    },
    [windLimit, waveLimit],
  )

  return (
    <DataGrid
      density="compact"
      style={{ height: "100%", width: "100%", borderRadius: "0px" }}
      sx={GRID_SX}
      rows={rows}
      columns={COLUMNS}
      disableColumnMenu
      disableColumnFilter
      disableColumnSelector
      disableColumnSorting
      isCellEditable={() => false}
      getRowClassName={rowClassName}
      getRowId={(row) => {
        const num = row.properties?.number
        const ext = row.properties?.extended_number ?? 0
        if (num != null) return `${num}.${ext}`
        if (row.properties?.timestamp) return row.properties.timestamp
        if (row.geometry?.coordinates)
          return `${row.geometry.coordinates[0]}_${row.geometry.coordinates[1]}`
        return JSON.stringify(row)
      }}
    />
  )
}
